import { types } from 'mobx-state-tree'
import { BookingModelBase } from './BookingModel.base'
import { nightCount, formatDate } from 'utils/date'
import clamp from 'lodash-es/clamp'
import moment from 'moment'
import { momentDate } from 'store/types'
import {
  formatCurrency,
  formatDollars,
  getCCType,
  rsaEncrypt,
} from 'utils/currency'

/* A graphql query fragment builders for BookingModel */
export {
  selectFromBooking,
  bookingModelPrimitives,
  BookingModelSelector,
} from './BookingModel.base'

/**
 * BookingModel
 */
export const BookingModel = BookingModelBase.props({
  arrivalDate: types.union(types.undefined, types.null, momentDate),
  departureDate: types.union(types.undefined, types.null, momentDate),
  bookedAt: types.union(types.undefined, types.null, momentDate),
  heldAt: types.union(types.undefined, types.null, momentDate),
  createdAt: types.union(types.undefined, types.null, momentDate),
  updatedAt: types.union(types.undefined, types.null, momentDate),
})
  .views((self) => ({
    get store() {
      return self.__getStore()
    },
    get booking() {
      return self.store.booking
    },
    get isSearched() {
      return self.availabilities.length > 0 && self.reasonCodes.length === 0
    },
    get isQuoted() {
      return !!self.bookingQuoteId
    },
    get isBooked() {
      return self.bookingSuccess && !!self.bookingUuid
    },
    get isCancelled() {
      return !!self.cancelledAt
    },
    get expiresAt() {
      return moment(self.heldAt).add(self.holdDuration, 'minute')
    },
    get isExpired() {
      return self.heldAt ? moment().isAfter(self.expiresAt) : true
    },
    get isOnHold() {
      return self.holdSuccess && !self.isExpired
    },
    get minArrivalDate() {
      const nextBookingDate = self.store.park.nextBookingDate || new Date()
      return moment(nextBookingDate)
        .add(self.store.park.leadDays, 'day')
        .startOf('day')
    },
    get maxArrivalDate() {
      return moment().add(1, 'year').endOf('day')
    },
    get minDepartureDate() {
      const minDate = self.arrivalDate
        ? moment(self.arrivalDate)
        : self.minArrivalDate
      return moment(minDate).add(1, 'day')
    },
    get maxDepartureDate() {
      const maxDate = self.arrivalDate
        ? moment(self.arrivalDate)
        : self.maxArrivalDate
      return moment(maxDate).add(self.park.maxNights, 'day')
    },
    get formattedArrivalDate() {
      return formatDate(self.arrivalDate)
    },
    get formattedDepartureDate() {
      return formatDate(self.departureDate)
    },
    get numberOfNights() {
      const non = clamp(
        nightCount(self.arrivalDate, self.departureDate),
        0,
        10000
      )
      return non ? non : ''
    },
    get unitTypeName() {
      return (
        self.unitTypeId &&
        self.park.unitTypes.find((ut) => ut.id === self.unitTypeId)?.name
      )
    },
    get currentAvailability() {
      return (
        self.availabilityId &&
        self.availabilities.find((av) => av.id === self.availabilityId)
      )
    },
    get reasonCodes() {
      return self.availabilities.length > 0
        ? self.availabilities.map((av) => av.reasonCodes.slice()).flat()
        : []
    },
    get availabilityDescription() {
      return self.currentAvailability?.description
    },
    get currentBookingOptions() {
      return (
        (self.currentAvailability &&
          self.currentAvailability.bookingOptions.filter(
            (bo) => bo.description !== 'BYS'
          )) ||
        []
      )
    },
    get currentNumericBookingOptions() {
      return self.currentBookingOptions.filter((bo) => bo.fieldType === 0)
    },
    get currentBooleanBookingOptions() {
      return self.currentBookingOptions.filter((bo) => bo.fieldType === 1)
    },
    bookingOptionById(id) {
      return self.currentAvailability.bookingOptions.find((bo) => bo.id === id)
    },
    get tripDetails() {
      const getTripDetails = ({
        arrivalDate,
        departureDate,
        unitTypeId,
        unitLength,
      }) => {
        const arrival = arrivalDate?.format('yyyy-MM-DD')
        const departure = departureDate?.format('yyyy-MM-DD')
        return {
          arrivalDate: arrival,
          departureDate: departure,
          unitTypeId,
          unitLength,
        }
      }
      return getTripDetails(self)
    },
    get pickSite() {
      const getPickSite = ({ availabilityId }) => ({
        availabilityId,
        numericBookingOptions: self.currentNumericBookingOptions.map((bo) =>
          bo.toJSON()
        ),
        booleanBookingOptions: self.currentBooleanBookingOptions.map((bo) =>
          bo.toJSON()
        ),
      })
      return getPickSite(self)
    },
    get checkout() {
      const getCheckout = ({
        firstName,
        lastName,
        address,
        address2,
        city,
        state,
        postalCode,
        country,
        email,
        phone,
        notes,
        ccType,
        ccExpiry,
        ccEnc,
        ccNum4,
        memberId,
        discountCode,
        discountText,
        loyaltyCode,
        loyaltyText,
        availableSites,
      }) => ({
        firstName,
        lastName,
        address,
        address2,
        city,
        state,
        postalCode,
        country,
        email,
        phone,
        notes: notes || '',
        termsAccepted: true,
        ccType,
        ccExpiry,
        ccEnc,
        ccNum4,
        memberId,
        discountCode,
        discountText,
        loyaltyCode,
        loyaltyText,
        sites: [availableSites[0]],
      })
      return getCheckout(self)
    },
    get depositRequired() {
      return formatCurrency(
        self.depositRequiredCents,
        self.depositRequiredCurrency
      )
    },
    get subtotal() {
      return formatCurrency(self.subtotalCents, self.subtotalCurrency)
    },
    get transactionFee() {
      return formatCurrency(
        self.transactionFeeCents,
        self.transactionFeeCurrency
      )
    },
    get tax() {
      return formatCurrency(self.taxCents, self.taxCurrency)
    },
    get total() {
      return formatCurrency(self.totalCents, self.totalCurrency)
    },
    get holdDeposit() {
      return formatCurrency(self.holdDepositCents, self.holdDepositCurrency)
    },
    get holdTotal() {
      return formatCurrency(self.holdTotalCents, self.holdTotalCurrency)
    },
    get depositRequiredDollars() {
      return formatDollars(
        self.depositRequiredCents,
        self.depositRequiredCurrency
      )
    },
    get subtotalPerNightCents() {
      if (self.subtotalCents > 0 && self.numberOfNights > 0) {
        return self.subtotalCents / self.numberOfNights
      } else {
        return 0
      }
    },
    get subtotalPerNight() {
      return formatCurrency(self.subtotalPerNightCents, self.subtotalCurrency)
    },
    get subtotalPerNightDollars() {
      return formatDollars(self.subtotalPerNightCents, self.subtotalCurrency)
    },
    get subtotalDollars() {
      return formatDollars(self.subtotalCents, self.subtotalCurrency)
    },
    get transactionFeeDollars() {
      return formatDollars(
        self.transactionFeeCents,
        self.transactionFeeCurrency
      )
    },
    get taxDollars() {
      return formatDollars(self.taxCents, self.taxCurrency)
    },
    get totalDollars() {
      return formatDollars(self.totalCents, self.totalCurrency)
    },
    get holdDepositDollars() {
      return formatDollars(self.holdDepositCents, self.holdDepositCurrency)
    },
    get holdTotalDollars() {
      return formatDollars(self.holdTotalCents, self.holdTotalCurrency)
    },
  }))
  .actions((self) => ({
    // This is an auto-generated example action.
    log() {
      console.log(JSON.stringify(self))
    },
    encryptPayload: (val) => {
      const cleansed = val.replace(/\s+/g, '')
      console.log(cleansed)
      return rsaEncrypt(self.publicKey, val)
    },
    setCcEnc: (val) => {
      console.log(val)
      self.ccNum4 = val ? val.slice(-4) : ''
      self.ccEnc = val ? self.encryptPayload(val) : ''
      self.ccType = getCCType(val)
    },
    setCcExpiry: (val) => {
      self.ccExpiry = val
    },
    setValue(name, value) {
      self[name] = value
    },
    setBookingOptionQuantity(id, quantity) {
      const bo = self.bookingOptionById(id)
      bo.setQuantity(quantity)
    },
  }))
